import { Injectable } from '@angular/core';
import { BudsenseFile } from '../../../../../models/shared/budsense-file';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { UserDomainModel } from '../../../../../domainModels/user-domain-model';
import { BsError } from '../../../../../models/shared/bs-error';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { Asset } from '../../../../../models/image/dto/asset';
import { BaseModalViewModel } from '../../../../../models/base/base-modal-view-model';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../../../../../modules/toasts/service/toast.service';
import { AllowedUploadTypesConfig, UploadUtils } from '../../../../../utils/upload-utils';
import { catchError, map, take } from 'rxjs/operators';
import {
  AssetAssignmentSession,
  AssetAssignmentSessionConfig
} from '../../../../../models/shared/asset-assignment-session';
import {
  AssetAssignmentReconciliationHandler,
  AssignedAssetSnapshot
} from '../../../../../models/protocols/asset-assignment-reconciliation';
import { ASSET_ASSIGNMENT_TIMEOUT_MS, ASSET_RETRY_COUNT, ASSET_RETRY_DELAY } from '../../../../../utils/media-utils';
import { exists } from '../../../../../functions/exists';
import { MediaManagerMultiUploadItemState } from '../../../../../models/media-manager/shared/media-manager-multi-upload-state';

@Injectable()
export class UploadProfilePictureViewModel extends BaseModalViewModel {
  constructor(
    private userDomainModel: UserDomainModel,
    private toastService: ToastService,
    router: Router,
    ngbModal: NgbModal
  ) {
    super(router, ngbModal);
  }

  existingProfilePicture: Asset;
  file: BudsenseFile;
  asset: Asset;
  protected override _loadingOpts = new BehaviorSubject(this.getLoadingSpinner());

  public override dismissModalSubject: Subject<boolean> = new Subject<boolean>();
  private readonly allowedUploadTypesConfig$: Observable<AllowedUploadTypesConfig> = of({
    allowImage: true,
    allowVideo: false,
    allowPdf: false
  });
  private readonly profilePictureAssignmentSession = new AssetAssignmentSession(this.buildAssignmentSessionConfig());
  public readonly uploadItemState$ = this.profilePictureAssignmentSession.uploadItemState$;
  public readonly showUploadStatus$ = this.profilePictureAssignmentSession.showUploadStatus$;
  public readonly deleteProfilePictureDisabled$ = this.uploadItemState$.pipe(
    map(state => exists(state) && !UploadUtils.isUploadItemFinalStatus(state.status))
  );

  private getLoadingSpinner(): LoadingOptions {
    const opts = LoadingOptions.default();
    opts.loadingText = 'Uploading';
    opts.isLoading = false;
    opts.fullscreen = false;
    return opts;
  }

  uploadProfilePicture() {
    if (!this.file && !this.asset) return;
    if (this.file) {
      this.profilePictureAssignmentSession.startFileAssignment(this.file, file =>
        this.performProfilePictureUpload$(file)
      );
      return;
    }
    this.profilePictureAssignmentSession.startMediaManagerAssignment(this.asset, asset =>
      this.performProfilePictureCopy$(asset)
    );
  }

  deleteProfilePicture() {
    this.uploadItemState$.once(state => {
      if (this.profilePictureDeleteDisabled(state)) return;
      this.deleteProfilePictureIfAllowed();
    });
  }

  private deleteProfilePictureIfAllowed(): void {
    const lm = 'Deleting Profile Picture';
    if (!this._loadingOpts.containsRequest(lm)) {
      this._loadingOpts.addRequest(lm);
      this.userDomainModel.deleteProfilePicture$().subscribe({
        next: () => {
          this._loadingOpts.removeRequest(lm);
          this.dismissModalSubject.next(true);
          this.toastService.publishSuccessMessage('Profile picture deleted');
        },
        error: (error: BsError) => {
          this._loadingOpts.removeRequest(lm);
          this.toastService.publishError(error);
          throwError(() => error);
        }
      });
    }
  }

  public clearUploadItemState(): void {
    this.profilePictureAssignmentSession.clearUploadItemState();
  }

  private profilePictureDeleteDisabled(state: MediaManagerMultiUploadItemState | null): boolean {
    return exists(state) && !UploadUtils.isUploadItemFinalStatus(state.status);
  }

  private buildAssignmentSessionConfig(): AssetAssignmentSessionConfig {
    return {
      destroy$: this.onDestroy,
      allowedUploadTypesConfig$: this.allowedUploadTypesConfig$,
      reconciliationHandler: this.buildProfilePictureReconciliationHandler(),
      assignmentSignalTimeoutMs: ASSET_ASSIGNMENT_TIMEOUT_MS,
      pollRetryCount: ASSET_RETRY_COUNT,
      pollRetryDelayMs: ASSET_RETRY_DELAY * 1000,
      onActionFailed: error => this.toastService.publishError(error),
      assignmentTimedOutMessage: 'Profile picture update timed out.',
      uploadFailedMessage: 'Profile picture upload failed.',
      copyFailedMessage: 'Profile picture copy failed.'
    };
  }

  private buildProfilePictureReconciliationHandler(): AssetAssignmentReconciliationHandler {
    return {
      getReconciliationContext$: () =>
        this.userDomainModel.user$.pipe(
          take(1),
          map(user => ({ userId: user?.userId ?? '', locationId: 0, hasTarget: exists(user) }))
        ),
      getAssignedAssetSnapshot$: () =>
        this.userDomainModel.user$.pipe(
          take(1),
          map(user => this.getProfilePictureSnapshot(user?.profilePicture))
        ),
      getAssignedAsset$: () =>
        this.userDomainModel.user$.pipe(
          take(1),
          map(user => user?.profilePicture)
        ),
      refreshAssignedAsset$: () => this.userDomainModel.refreshUser$().pipe(map(user => user?.profilePicture)),
      buildAssignmentSignal$: () => this.userDomainModel.user$.pipe(map(() => undefined as void)),
      onAssignmentConfirmed: () => this.completeProfilePictureAssignment()
    };
  }

  private getProfilePictureSnapshot(asset: Asset | null | undefined): AssignedAssetSnapshot {
    return {
      assetId: asset?.id,
      timestamp: asset?.timestamp
    };
  }

  private performProfilePictureUpload$(file: BudsenseFile): Observable<string[]> {
    return this.userDomainModel.uploadProfilePicture$(file).pipe(
      map(() => [file.name]),
      catchError((err: BsError) => throwError(() => err))
    );
  }

  private performProfilePictureCopy$(asset: Asset): Observable<string[]> {
    return this.userDomainModel.copyProfilePictureToTarget$(asset).pipe(
      map(() => [asset?.fileName]),
      catchError((err: BsError) => throwError(() => err))
    );
  }

  private completeProfilePictureAssignment(): void {
    this.file = undefined;
    this.asset = undefined;
    this.clearUploadItemState();
    this.dismissModalSubject.next(true);
    this.toastService.publishSuccessMessage('Profile picture uploaded');
  }
}
